import { memo, type MutableRefObject } from 'react';

import { HighlightedPointCanvas } from './HighlightedPointCanvas.js';
import { datapointIndicatorShiftedLayout } from './XYChartExplore.sty.js';
import { Canvas } from '../../../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../../../core/components/canvas/CanvasContext.js';
import type { CanvasShape } from '../../../../core/components/canvas/types/canvas-shape.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { POINT_OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import { getGroupedDataPointsByCoordinate } from '../../../../core/utils/group-by-threshold.js';
import { useAreaData } from '../../../hooks/useAreaData.js';
import { useDotData } from '../../../hooks/useDotData.js';
import { useLineData } from '../../../hooks/useLineData.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartLinearSelection } from '../../../hooks/useXYChartLinearSelection.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { useXYChartTooltip } from '../../../hooks/useXYChartTooltip.js';
import { useSelectState } from '../../../store/selectors.js';
import { useTooltipMetadata } from '../hooks/useTooltipMetadata.js';
import {
  buildContentHighlightedPoint,
  buildSelectionHighlightedPoints,
} from '../utils/get-selected-datapoints.js';

export const XYChartLinearPoint = memo(() => {
  const allLinearDatapoints = useTooltipMetadata('linear');
  const { variant } = useXYChartTooltip();
  const selectState = useSelectState();
  const { width, height } = useGridPlotSize();
  const axisIdToScales = useXYChartScales();
  const linearSelection = useXYChartLinearSelection();
  const { axisIdToScaleDomain } = useXYChartData();
  const { axes, datapointMetadataMap } = useXYChartData();

  const {
    seriesToMetadata: dotSeriesMeta,
    filteredSeriesWithDatapoints: dotSeriesWithDatapoints,
  } = useDotData();
  const {
    seriesToMetadata: lineSeriesMeta,
    filteredSeriesWithDatapoints: lineSeriesWithDatapoints,
  } = useLineData();
  const {
    seriesToMetadata: areaSeriesMeta,
    filteredSeriesWithDatapoints: areaSeriesWithDatapoints,
  } = useAreaData();

  const showContentPoint = allLinearDatapoints.length > 0;

  const showPoint =
    !showContentPoint &&
    (!selectState || linearSelection.selectionConfigByAxisId.size === 0);

  if (showPoint) {
    return null;
  }

  const selectionPoints = buildSelectionHighlightedPoints({
    selectState,
    linearSelection,
    width,
    axisIdToScales,
    dotSeriesWithDatapoints,
    lineSeriesWithDatapoints,
    areaSeriesWithDatapoints,
    dotSeriesMeta,
    lineSeriesMeta,
    areaSeriesMeta,
    axisIdToScaleDomain,
    axes,
    datapointMetadataMap,
  });

  const contentPoints = allLinearDatapoints.flatMap((contentState) =>
    buildContentHighlightedPoint({
      contentState,
      axisIdToScales,
      dotSeriesMeta,
      lineSeriesMeta,
      areaSeriesMeta,
      axisIdToScaleDomain,
      datapointMetadataMap,
    }),
  );

  // The lollipop pill (grouping close hover points) is a shared-tooltip
  // feature only. In the default variant each hover point keeps its own
  // single-circle highlight, so it is wrapped as a singleton group.
  const hoverGroups =
    variant === 'shared'
      ? getGroupedDataPointsByCoordinate(contentPoints, 'y')
      : contentPoints.map((point) => [point]);

  if (selectionPoints.length === 0 && hoverGroups.length === 0) {
    return null;
  }

  return (
    <div className={datapointIndicatorShiftedLayout}>
      <CanvasContext.Provider
        value={{
          shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
        }}
      >
        <Canvas
          width={width + 2 * POINT_OVERLAY_PADDING}
          height={height + 2 * POINT_OVERLAY_PADDING}
        >
          <HighlightedPointCanvas
            selectionPoints={selectionPoints}
            hoverGroups={hoverGroups}
          />
        </Canvas>
      </CanvasContext.Provider>
    </div>
  );
});

XYChartLinearPoint['displayName'] = 'XYChartLinearPoint';
